/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  findLatestPricing5PlanId,
  PRICING_5_X_EARLY_STAGE_SOLUTION_ID,
  percentDiscountForSolution,
} from 'embercom/lib/billing';
import type CardlessTrialService from 'embercom/services/cardless-trial-service';
import type QuoteService from 'embercom/services/quote-service';
import type ModalService from 'embercom/services/modal-service';

export default class CardlessConversionModalService extends Service {
  @tracked showModal = false;
  @tracked modalDismissedWhenTrialExpiredNonStorage = false;
  @tracked afterCloseModal: (() => void) | undefined;
  @tracked customerWantsToExtendTrial = false;

  @service declare quoteService: QuoteService;
  @service declare cardlessTrialService: CardlessTrialService;
  @service declare appService: AppService;
  @service declare experimentOptInService: any;
  @service declare customerService: any;
  @service declare modalService: ModalService;

  @action
  setCustomerWantsToExtendTrial(value: boolean) {
    this.customerWantsToExtendTrial = value;
  }

  async toggle() {
    this.modalService.openModal(
      'react/cardless-conversion-modal-pricing5',
      {
        onOpenChange: () => {
          this.modalService.closeModal();
        },
        activePlanId: findLatestPricing5PlanId(
          this.cardlessTrialService.activeOrExpiredPlanTrialIds,
        ),
        oneDollarTrialExtension: this.showExtendedTrialState,
        selfServe:
          !this.customerService.customer.inSalesProspectTrial &&
          this.appService.app.isNotSalesforceContracted,
        canUseEarlyStageDetection: this.canUseEarlyStageDetection,
        canUseProductSpecialistCTAs: this.canUseProductSpecialistCTAs,
        earlyStageDiscount: this.earlyStageDiscount,
      },
      {
        showIntercomWidget: true,
      },
    );
  }

  get customerCanExtendTrial() {
    return !!(
      (this.customerService.customer.inCardlessTrial ||
        this.customerService.customer.inExpiredCardlessTrial) &&
      !this.customerService.customer.inTrialExtension &&
      !this.customerService.customer.inExpiredTrialExtension
    );
  }

  get showExtendedTrialState() {
    return !!(
      this.customerCanExtendTrial &&
      this.customerWantsToExtendTrial &&
      this.appService.app?.teamPurchaseExperienceTrialExtensionsVariant
    );
  }

  get canUseProductSpecialistCTAs() {
    return this.cardlessTrialService.canUseProductSpecialistCTAs;
  }

  get canUseEarlyStageDetection() {
    return this.cardlessTrialService.canUseEarlyStageDetection;
  }

  get earlyStageDiscount() {
    return percentDiscountForSolution(PRICING_5_X_EARLY_STAGE_SOLUTION_ID);
  }
}

declare module '@ember/service' {
  interface Registry {
    cardlessConversionModalService: CardlessConversionModalService;
    'cardless-conversion-modal-service': CardlessConversionModalService;
  }
}
